import React, { useContext } from 'react';
import { Info } from 'lucide-react';

import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Label } from '@epoch-ui/components/Form';
import { Banner, BannerIcon } from '@epoch-ui/components/Banner';
import UserContext from 'context/userContext';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { formatNumberWithComma } from 'utils/format';

import { useInviteGuestsModalContext } from '../hooks/useInviteGuestsModalContext';

import { SelectedGuestsList } from './SelectedGuestsList';

interface InviteSettingsStepProps {
  expectedInviteeTotal: number;
  calendarEventAttendeeThreshold: number;
  largeCalendarSplitEnabled: boolean;
}

export const InviteSettingsStep: React.FC<InviteSettingsStepProps> = ({
  expectedInviteeTotal,
  calendarEventAttendeeThreshold,
  largeCalendarSplitEnabled
}) => {
  const {
    calendarEmailAvailable,
    sendCalendarEmail,
    sendSlackInvite,
    setSendCalendarEmail,
    setSendSlackInvite
  } = useInviteGuestsModalContext();

  const user = useContext(UserContext);
  const event = useEditableEvent();

  const usesOutlook = !!user?.companyUsesOutlookCalendar;

  const showLargeCalendarSplitBanner =
    largeCalendarSplitEnabled &&
    event.calendarNotifications &&
    expectedInviteeTotal > calendarEventAttendeeThreshold;

  const calendarProviderName = usesOutlook ? 'Outlook' : 'Google';
  const creatorName = event.createdBy
    ? `${event.createdBy?.firstName ?? ''} ${event.createdBy?.lastName ?? ''}`
    : '';

  return (
    <div className="tw-flex tw-h-full tw-min-h-64 tw-w-full tw-flex-row tw-gap-4">
      {/* Left Panel - Selected Guests (Read-only) */}
      <div className="tw-flex tw-h-full tw-w-5/12 tw-flex-col tw-overflow-y-auto tw-rounded-2xl tw-bg-background-grey tw-p-6">
        <SelectedGuestsList readOnly />
      </div>

      {/* Right Panel - Notification Settings */}
      <div className="tw-flex tw-w-7/12 tw-flex-1 tw-flex-col tw-gap-4">
        <Label className="tw-text-sm tw-font-medium">Send invitation notifications?</Label>

        {/* Large calendar split warning banner */}
        {showLargeCalendarSplitBanner && (
          <Banner variant="warning">
            <BannerIcon icon={Info} />
            <p className="tw-m-0 tw-text-sm">
              This event has more than {formatNumberWithComma(calendarEventAttendeeThreshold)}{' '}
              guests. To ensure calendar performance, guests will be distributed across{' '}
              {Math.ceil(expectedInviteeTotal / calendarEventAttendeeThreshold)} calendar events.
              <a
                href="https://help.epochapp.com/articles/why-are-guests-distributed-across-multiple-calendar-events"
                target="_blank"
                rel="noreferrer"
              >
                <span className="tw-mb-0 tw-ml-1 tw-leading-5 tw-text-yellow-warning tw-underline ">
                  Learn more
                </span>
              </a>
            </p>
          </Banner>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2">
          {/* Calendar Email Checkbox */}
          <div className="tw-flex tw-items-center tw-gap-3">
            <Checkbox
              id="calendar-email"
              checked={sendCalendarEmail}
              disabled={usesOutlook || !calendarEmailAvailable}
              onCheckedChange={(checked) => setSendCalendarEmail(!!checked)}
            />
            <Label htmlFor="calendar-email" className="tw-m-0 ">
              Email from {calendarProviderName} Calendar
              {creatorName && <span className="tw-text-primary-grey"> ({creatorName})</span>}
              {!event.calendarNotifications && (
                <p className="tw-m-0 tw-text-xs tw-text-primary-grey">
                  No calendar event created for this event
                </p>
              )}
              {event.calendarNotifications && !event.sendCalendarInvites && (
                <p className="tw-m-0 tw-text-xs tw-text-primary-grey">
                  Guests are added to the calendar after they RSVP
                </p>
              )}
              {usesOutlook && (
                <p className="tw-m-0 tw-text-xs tw-text-primary-grey">Required for Outlook</p>
              )}
            </Label>
          </div>

          {/* Slack Checkbox */}
          <div className="tw-flex tw-items-center tw-gap-3">
            <Checkbox
              id="slack-invite"
              checked={sendSlackInvite}
              onCheckedChange={(checked) => setSendSlackInvite(!!checked)}
            />
            <Label htmlFor="slack-invite" className="tw-m-0 ">
              Slack
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};
